<script lang="ts" setup>
import { computed, ref, shallowRef, watch } from "vue";
import { Marker as MarkerItem, MarkerCluster } from "vue3-google-map";
import type { MarkerClustererOptions } from "@googlemaps/markerclusterer";
import { SuperClusterViewportAlgorithm } from "@googlemaps/markerclusterer";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import { useGoogleMap } from "@/composables/useGoogleMap";
import ContainerLayout from "@/components/layout/ContainerLayout.vue";
import { useI18n } from "vue-i18n";
import { USA_MAP_CENTER } from "@/constants/map";
import { usePaginatedLoader } from "@/composables/reactive/usePaginatedLoader";
import LoadingIcon from "@/components/icons/LoadingIcon.vue";
import TrucksFilterSidebar from "@/views/fleets/map/components/TrucksFilterSidebar.vue";
import type { GoogleMapCoordinates } from "@/api/dto/coordinates.dto";
import TruckMarker from "@/views/fleets/map/components/TruckMarker.vue";
import type { TruckLocationSource } from "@/types/trucks";
import OmniGoogleMap from "@/components/omni/OmniGoogleMap.vue";
import { useBaseView } from "@/composables";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useAppStore } from "@/store/app.store";
import TrucksMapLegendContent from "@/views/fleets/map/components/TrucksMapLegendContent.vue";

const api = new API();

const appStore = useAppStore();

const { showAPIError } = useError();
const { getApiKey, getMapId } = useGoogleMap();
const { t } = useI18n();
const { loadAll, isLoading: loadingAllTrucks, isAllLoaded: isAllTruckLoaded } = usePaginatedLoader();
const { hasAccess } = useBaseView();

const mapZoom = ref<number>(4);
const mapCenter = ref(USA_MAP_CENTER);
const trucksWithCoords = shallowRef<TruckDto[]>([]);
const searchedLocation = ref<GoogleMapCoordinates | null>(null);
const locationSource = ref<TruckLocationSource>("current");

const algorithmOptions = {
  radius: 200,
  maxZoom: 10,
  minPoints: 4,
  viewportPadding: 0,
};

const clusterOptions: MarkerClustererOptions = {
  algorithm: new SuperClusterViewportAlgorithm(algorithmOptions),
};

const hasAccessToTrucks = computed(() => {
  return hasAccess(DispatcherPermissionValue.MapShowTrucks);
});

async function onLocationSearch(coordinates: GoogleMapCoordinates) {
  mapZoom.value = 8;
  searchedLocation.value = coordinates;
}

async function getAllTrucks() {
  try {
    await loadAll<TruckDto>(
      async (offset, pageSize) => {
        const page = Math.floor(offset / pageSize) + 1;
        const response = await api.trucks.findTrucks({ page, pageSize }, false);

        if (response.errorData) {
          showAPIError(response);
          return null;
        }

        return response?.data?.items ?? [];
      },
      {
        pageSize: 300,
        onBatch: (items) => {
          const withCoords = items.filter((t) => {
            if (locationSource.value === "current") {
              return t.currentLocation?.coordinates;
            }

            return t.availableLocation?.coordinates;
          });

          if (!withCoords.length) {
            return;
          }

          trucksWithCoords.value = [...trucksWithCoords.value, ...withCoords];
        },
      },
    );
  } catch (error) {
    console.error(error);
  }
}

watch(
  () => appStore.dispatcher,
  async (value) => {
    if (value) {
      if (hasAccessToTrucks.value) {
        await getAllTrucks();
      }
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <container-layout :header="t('trucks.live-map')">
    <div class="trucks-map">
      <omni-google-map
        :api-key="getApiKey()"
        :center="mapCenter"
        :disable-default-ui="true"
        :map-id="getMapId()"
        :zoom="mapZoom"
      >
        <marker-cluster :options="clusterOptions" :render-debounce-delay="100">
          <truck-marker
            v-for="truck in trucksWithCoords"
            :key="truck.id"
            :location-source="locationSource"
            :truck="truck"
          />
        </marker-cluster>

        <marker-item v-if="searchedLocation" :options="{ position: searchedLocation }" />

        <template #mapLegend>
          <trucks-map-legend-content />
        </template>
      </omni-google-map>

      <div v-if="loadingAllTrucks" class="trucks-map__loader">
        <div class="d-flex align-center justify-center col-gap-3">
          <loading-icon />

          <span>{{ t("trucks.loading-trucks-facilities") }}</span>
        </div>
      </div>

      <trucks-filter-sidebar
        v-model:location-source="locationSource"
        v-model:map-center="mapCenter"
        v-model:trucks="trucksWithCoords"
        :is-trucks-loaded="isAllTruckLoaded"
        class="trucks-map__filters"
        @update:map-center="onLocationSearch"
      />
    </div>
  </container-layout>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trucks-map {
  position: relative;
  height: 100%;

  &__loader {
    padding: 12px;
    background-color: $white;
    position: absolute;
    bottom: 24px;
    right: 24px;
    z-index: 1;
    border: 1px solid $primary;
    border-radius: 8px;
    color: $primary;
  }

  &__filters {
    position: absolute;
    top: 24px;
    left: 24px;
  }
}
</style>
